import React, { createContext, useContext, useMemo, useRef, useState } from "react";

type SnackbarVariant = "success" | "error" | "info" | "warning";

export type SnackbarOptions = {
    variant?: SnackbarVariant;
    title?: string;
    message: string;
    durationMs?: number; // default 3500
};

type SnackbarItem = {
    id: string;
    variant: SnackbarVariant;
    title?: string;
    message: string;
    durationMs: number;
};

type SnackbarContextValue = {
    push: (opts: SnackbarOptions) => string;
    dismiss: (id: string) => void;
    clear: () => void;
};

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

export function useSnackbar() {
    const ctx = useContext(SnackbarContext);
    if (!ctx) throw new Error("useSnackbar must be used within SnackbarProvider");
    return ctx;
}

function uid() {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function SnackbarProvider({ children }: { children: React.ReactNode }) {
    const [items, setItems] = useState<SnackbarItem[]>([]);
    const timers = useRef<Map<string, number>>(new Map());

    const dismiss = (id: string) => {
        const t = timers.current.get(id);
        if (t) window.clearTimeout(t);
        timers.current.delete(id);
        setItems((prev) => prev.filter((x) => x.id !== id));
    };

    const clear = () => {
        timers.current.forEach((t) => window.clearTimeout(t));
        timers.current.clear();
        setItems([]);
    };

    const push = (opts: SnackbarOptions) => {
        const id = uid();
        const item: SnackbarItem = {
            id,
            variant: opts.variant ?? "info",
            title: opts.title,
            message: opts.message,
            durationMs: opts.durationMs ?? 3500,
        };

        setItems((prev) => [item, ...prev].slice(0, 5));

        const timer = window.setTimeout(() => dismiss(id), item.durationMs);
        timers.current.set(id, timer);

        return id;
    };

    const value = useMemo(() => ({ push, dismiss, clear }), []);

    return (
        <SnackbarContext.Provider value={value}>
            {children}
            <SnackbarViewport
                items={items}
                onDismiss={dismiss}
                onPause={(id) => {
                    const t = timers.current.get(id);
                    if (t) window.clearTimeout(t);
                }}
                onResume={(id, remainingMs) => {
                    const timer = window.setTimeout(() => dismiss(id), remainingMs);
                    timers.current.set(id, timer);
                }}
            />
        </SnackbarContext.Provider>
    );
}

function variantStyles(v: SnackbarVariant) {
    switch (v) {
        case "success":
            return { ring: "ring-emerald-400/30", dot: "bg-emerald-400", title: "text-emerald-100" };
        case "error":
            return { ring: "ring-red-400/30", dot: "bg-red-400", title: "text-red-100" };
        case "warning":
            return { ring: "ring-yellow-400/30", dot: "bg-yellow-400", title: "text-yellow-100" };
        default:
            return { ring: "ring-sky-400/30", dot: "bg-sky-400", title: "text-sky-100" };
    }
}

function SnackbarViewport({
    items,
    onDismiss,
    onPause,
    onResume,
}: {
    items: SnackbarItem[];
    onDismiss: (id: string) => void;
    onPause: (id: string) => void;
    onResume: (id: string, remainingMs: number) => void;
}) {
    // хранение времени "остатка" на item (без re-render каждый тик)
    const startAt = useRef<Map<string, number>>(new Map());
    const remaining = useRef<Map<string, number>>(new Map());

    return (
        <div className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-[min(420px,calc(100vw-2rem))] flex-col gap-2">
            {items.map((item) => {
                const s = variantStyles(item.variant);

                // init remaining
                if (!remaining.current.has(item.id)) remaining.current.set(item.id, item.durationMs);

                return (
                    <div
                        key={item.id}
                        className={[
                            "pointer-events-auto rounded-xl bg-black/80 backdrop-blur",
                            "ring-1 shadow-lg",
                            s.ring,
                            "px-4 py-3",
                            "animate-[snack-in_180ms_ease-out]",
                        ].join(" ")}
                        onMouseEnter={() => {
                            onPause(item.id);
                            const started = startAt.current.get(item.id);
                            if (started) {
                                const elapsed = Date.now() - started;
                                const left = Math.max(0, (remaining.current.get(item.id) ?? item.durationMs) - elapsed);
                                remaining.current.set(item.id, left);
                            }
                        }}
                        onMouseLeave={() => {
                            const left = remaining.current.get(item.id) ?? item.durationMs;
                            startAt.current.set(item.id, Date.now());
                            onResume(item.id, left);
                        }}
                        onPointerDown={() => {
                            // чтобы при таче тоже работало "пауза"
                            onPause(item.id);
                            startAt.current.set(item.id, Date.now());
                        }}
                        onPointerUp={() => {
                            const left = remaining.current.get(item.id) ?? item.durationMs;
                            startAt.current.set(item.id, Date.now());
                            onResume(item.id, left);
                        }}
                    >
                        <div className="flex items-start gap-3">
                            <div className={"mt-1 h-2.5 w-2.5 shrink-0 rounded-full shadow-sm " + s.dot} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className={["text-sm font-semibold", s.title].join(" ")}>
                                            {item.title ?? defaultTitle(item.variant)}
                                        </div>
                                        <div className="mt-0.5 text-sm text-white/80 break-words">
                                            {item.message}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        aria-label="Close"
                                        className="rounded-md p-1 text-white/70 hover:text-white hover:bg-white/10 transition"
                                        onClick={() => onDismiss(item.id)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* reset hover timers on first paint */}
                        <InitTimer
                            id={item.id}
                            duration={remaining.current.get(item.id) ?? item.durationMs}
                            onStart={(id) => startAt.current.set(id, Date.now())}
                        />
                    </div>
                );
            })}

            <style>{`
        @keyframes snack-in {
          from { transform: translateY(-6px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
        </div>
    );
}

function defaultTitle(v: SnackbarVariant) {
    switch (v) {
        case "success":
            return "Success";
        case "error":
            return "Error";
        case "warning":
            return "Warning";
        default:
            return "Info";
    }
}

function InitTimer({ id, onStart }: { id: string; duration: number; onStart: (id: string) => void }) {
    // запускаем "старт" один раз, без useEffect зависимости по duration
    const did = useRef(false);
    if (!did.current) {
        did.current = true;
        onStart(id);
    }
    return null;
}